import { useState, useRef, useEffect } from "react";

interface ElementSize {
  width: number;
  height: number;
}

/**
 * Hook used to get the size of a certain element
 * @returns {[size, ref]} A tuple containing:
 * - `size`: The size of the element linked to the reference.
 * - `ref`: The reference to the specific element.
 */
const useElementSize = <T extends HTMLElement>(): [
  ElementSize,
  React.RefObject<T>
] => {
  const ref = useRef<T>(null);
  const [size, setSize] = useState<ElementSize>({ width: 0, height: 0 });

  useEffect(() => {
    // Check if there's a reference associated first
    if (!ref.current) return;

    // Set initial size
    setSize({
      width: ref.current.clientWidth,
      height: ref.current.clientHeight,
    });

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, []);

  return [size, ref];
};

export default useElementSize;
